/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  Controls,
  Background,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  Typography,
  Button,
  Select,
  MenuItem,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  Dialog,
  DialogTitle,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import "./diagram.css";
import axiosInstance, { fetcher, postFetcher } from "src/utils/axios";
import { useRouter } from "src/routes/hooks";
import Iconify from "src/components/iconify";
import { enqueueSnackbar } from "notistack";
import SaveWorkflowDialog from "./SaveWorkflowDialog";
import NodePropertiesDrawer3rdStep from "./NodePropertiesDrawer3rdStep";
import CalculateDialog from "./CalculateDialog";
import JobDialog from "./JobDialog";
import SmartView3rdStep from "./SmartView3rdStep";
import { useLocation } from "react-router";

const generateCustomId = (prefix = '') => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// ─── Shared styles ────────────────────────────────────────────────────────────
const BLUE = '#3b82f6';
const BLUE_LIGHT = '#eff6ff';
const BLUE_MID = '#dbeafe';

// ─── Regular / Default Node ───────────────────────────────────────────────────


export const CustomNodeComponent = ({ data, selected }) => {
  const inputs = data.input || [];
  const outputs = data.output || [];

  // Collect all product names from inputs
  const productNames = inputs.flatMap(i =>
    (i.product_data || []).map(p => p.name)
  );
  const outputProductNames = outputs.flatMap(o =>
    (o.product_data || []).map(p => p.name)
  );

  const hasMaterial = inputs.some(i => i.is_material);
  const hasUtility  = inputs.some(i => i.is_utility);

  return (
    <div style={{
      background: '#fff',
      position: 'relative',
      borderRadius: '14px',
      border: selected ? `2px solid ${BLUE}` : '1.5px solid #d1d9f0',
      boxShadow: selected
        ? '0 0 0 4px rgba(59,130,246,0.12), 0 4px 16px rgba(59,130,246,0.1)'
        : '0 2px 12px rgba(30,40,80,0.08)',
      minWidth: '220px',
      maxWidth: '280px',
      
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}>

      {/* Updated / Raw Material badges */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 8, right: 10, display: 'flex', gap: 6 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Left connection handle (target - incoming) */}
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{
          background: '#94a3b8', 
          border: '2px solid #fff',
          width: 10, 
          height: 10, 
          left: -6,
        }} 
      />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '12px 14px 10px',
        background: BLUE_LIGHT,
        borderBottom: `1px solid ${BLUE_MID}`,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '9px',
          background: BLUE, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          {/* Workflow icon */}
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5"/>
            <rect x="14" y="3" width="7" height="7" rx="1.5"/>
            <rect x="14" y="14" width="7" height="7" rx="1.5"/>
            <path d="M6.5 10v4h11v-4"/>
            <line x1="6.5" y1="14" x2="6.5" y2="17"/>
          </svg>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e293b', lineHeight: 1.2 }}>
            {data.label}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
            {data.type === 'default' ? 'Process Node' : data.type}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>

        {/* Input / Output counts */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Badge color="#3b82f6" bg="#eff6ff" label={`${inputs.length} Input${inputs.length !== 1 ? 's' : ''}`} />
          <Badge color="#10b981" bg="#ecfdf5" label={`${outputs.length} Output${outputs.length !== 1 ? 's' : ''}`} />
          {hasMaterial && <Badge color="#f59e0b" bg="#fffbeb" label="Material" />}
          {hasUtility  && <Badge color="#8b5cf6" bg="#f5f3ff" label="Utility" />}
        </div>

        {/* Input products */}
        {productNames.length > 0 && (
          <InfoRow icon="📦" label="Inputs" items={productNames} color="#3b82f6" />
        )}

        {/* Output products */}
        {outputProductNames.length > 0 && (
          <InfoRow icon="🏷️" label="Outputs" items={outputProductNames} color="#10b981" />
        )}

        {/* Empty state */}
        {productNames.length === 0 && outputProductNames.length === 0 && (
          <div style={{
            background: '#f8fafc', borderRadius: '8px', padding: '8px 10px',
            color: '#94a3b8', fontSize: '11.5px', textAlign: 'center',
            border: '1px dashed #e2e8f0',
          }}>
            Click to add details…
          </div>
        )}
      </div>

      {/* Right connection handle (source - outgoing) */}
      <Handle 
        type="source" 
        position={Position.Right} 
        style={{
          background: '#94a3b8', 
          border: '2px solid #fff',
          width: 10, 
          height: 10, 
          right: -6,
        }} 
      />
    </div>
  );
};


const PURPLE = '#7c3aed';
const PURPLE_LIGHT = '#f5f3ff';
const PURPLE_MID = '#ede9fe';
const PURPLE_BORDER = '#c4b5fd';
// ─── Diamond / Decision Node ──────────────────────────────────────────────────
export const DiamondNodeComponent = ({ data, selected }) => {
  console.log('Rendering DiamondNodeComponent with data:', data);

  const inputs  = data.input  || [];
  const outputs = data.output || []; // array of output IDs or objects
  const accent  = PURPLE;

  return (
    <div style={{
      background: '#fff',
      borderRadius: '16px',
      border: selected
        ? `2px solid ${accent}`
        : `1.5px solid ${PURPLE_BORDER}`,
      boxShadow: selected
        ? `0 0 0 4px ${accent}18, 0 4px 20px ${accent}18`
        : '0 2px 12px rgba(100,60,200,0.08)',
      minWidth: '230px',
      maxWidth: '290px',
      fontFamily: "'DM Sans', 'Geist', sans-serif",
      overflow: 'visible', // allow handles to peek outside
      cursor: 'pointer',
      position: 'relative',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}>

      {/* Updated / Raw Material badges for diamond nodes */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', gap: 6 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Top target handle (incoming connection) */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          top: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '11px',
        padding: '13px 15px 11px',
        background: PURPLE_LIGHT,
        borderBottom: `1px solid ${PURPLE_MID}`,
        borderRadius: '14px 14px 0 0',
      }}>
        {/* Icon */}
        <div style={{
          width: 38, height: 38, borderRadius: '11px',
          background: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}44`,
        }}>
          {/* Branch / split icon */}
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6"  cy="18" r="2"/>
            <circle cx="6"  cy="6"  r="2"/>
            <circle cx="18" cy="12" r="2"/>
            <path d="M6 8v8"/>
            <path d="M8 18h3a4 4 0 0 0 4-4V8"/>
            <path d="M15 6H8"/>
          </svg>
        </div>

        {/* Title */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e1b4b', lineHeight: 1.2 }}>
            {data.label || data.name}
          </div>
          <div style={{ fontSize: '11.5px', color: '#7c6faa', marginTop: '2px' }}>
            {inputs.length} input{inputs.length !== 1 ? 's' : ''} · {outputs.length} branch{outputs.length !== 1 ? 'es' : ''}
          </div>
        </div>
      </div>

      {/* ── Output rows ── */}
      <div style={{ padding: '10px 13px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {outputs.length === 0 ? (
          <div style={{
            background: '#f8f7ff',
            border: '1.5px dashed #c4b5fd',
            borderRadius: '10px',
            padding: '9px 12px',
            color: '#a78bfa',
            fontSize: '12px',
            textAlign: 'center',
          }}>
            No outputs connected
          </div>
        ) : (
          outputs.map((output, index) => {
            const outputId = typeof output === 'object' ? output.id : output;
            const outputLabel = typeof output === 'object' && output.name
              ? output.name
              : `Output ${index + 1}`;

            return (
              <div key={outputId ?? index} style={{ position: 'relative' }}>
                {/* Output row */}
                <div style={{
                  background: PURPLE_LIGHT,
                  border: `1.5px solid ${PURPLE_MID}`,
                  borderRadius: '10px',
                  padding: '9px 36px 9px 13px', // right padding for handle
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: accent, flexShrink: 0,
                      opacity: 0.7,
                    }} />
                    <span style={{
                      fontSize: '12.5px', fontWeight: 600,
                      color: '#2e1065',
                    }}>
                      {outputLabel}
                    </span>
                  </div>

                  {outputId && (
                    <span style={{
                      fontSize: '10px', color: '#a78bfa', fontWeight: 500,
                    }}>
                      #{outputId}
                    </span>
                  )}
                </div>

                {/* Per-output source handle on the right */}
                <Handle
                  type="source"
                  position={Position.Right}
                      id={`handle-output-${outputId ?? index}`} // unique handle ID

                  isConnectable={true}
                  data-output-index={index}
                  aria-label={`output-${String(outputId ?? index)}`}
                  style={{
                    background: accent,
                    border: '2.5px solid #fff',
                    width: 12, height: 12,
                    right: -7,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    boxShadow: `0 0 0 2px ${accent}44`,
                  }}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Input count badge (bottom-left) */}
      {inputs.length > 0 && (
        <div style={{
          position: 'absolute', bottom: -11, left: 16,
          background: accent, color: '#fff',
          fontSize: '10px', fontWeight: 700,
          borderRadius: '6px', padding: '2px 8px',
          boxShadow: `0 2px 6px ${accent}44`,
          border: '2px solid #fff',
        }}>
          ↓ {inputs.length} in
        </div>
      )}
    </div>
  );
};


// ─── Option Node ──────────────────────────────────────────────────
export const OptionNodeComponent = ({ data, selected, id }) => {
  console.log('Rendering OptionNodeComponent with data:', data, 'id:', id);

  const inputs = data.input || [];
  const outputs = data.output || [];
  
  // Get options from either options_config or options field
  const optionsConfig = data.options_config || data.options || {};
  const options = (optionsConfig.options || []).map(opt => ({
    ...opt,
    // Ensure each option has a selected property
    selected: opt.selected || false
  }));
  const selectionType = optionsConfig.selectionType || data.selectionType || 'radio';
  const accent = '#f97316'; // Orange accent for option nodes

  // Count selected options if checkbox
  const selectedCount = selectionType === 'checkbox' 
    ? options.filter(opt => opt.selected).length 
    : 0;

  // Get selected options for display
  const selectedOptions = options.filter(opt => opt.selected);

  return (
    <div style={{
      background: '#fff',
      borderRadius: '16px',
      border: selected
        ? `2px solid ${accent}`
        : `1.5px solid #fed7aa`,
      boxShadow: selected
        ? `0 0 0 4px ${accent}18, 0 4px 20px ${accent}18`
        : '0 2px 12px rgba(249,115,22,0.08)',
      minWidth: '250px',
      maxWidth: '320px',
      fontFamily: "'DM Sans', 'Geist', sans-serif",
      overflow: 'visible',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      position: 'relative',
    }}>

      {/* Updated / Raw Material badges - consistent with other nodes */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 8, right: 10, display: 'flex', gap: 6, zIndex: 10 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Left target handle (incoming connection) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          left: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />

      {/* Header - clicking here should select the node */}
      <div 
        style={{
          display: 'flex', alignItems: 'center', gap: '11px',
          padding: '13px 15px 11px',
          background: '#fff7ed',
          borderBottom: `1px solid #fed7aa`,
          borderRadius: '14px 14px 0 0',
          cursor: 'pointer',
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: '11px',
          background: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}44`,
        }}>
          {/* Choice / options icon */}
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="3" fill="#fff"/>
            <path d="M7 7l10 10M17 7l-10 10"/>
          </svg>
        </div>

        <div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#7c2d12', lineHeight: 1.2 }}>
            {data.label || data.name}
          </div>
          <div style={{ fontSize: '11.5px', color: '#9a3412', marginTop: '2px' }}>
            {selectionType === 'radio' ? '🔘 Single choice' : '☑️ Multiple choice'} · {options.length} option{options.length !== 1 ? 's' : ''}
            {selectedCount > 0 && ` (${selectedCount} selected)`}
          </div>
        </div>
      </div>

      {/* Options List */}
      <div style={{ padding: '12px 14px 14px' }}>
        {options.length === 0 ? (
          <div style={{
            background: '#f8f7ff',
            border: '1.5px dashed #fed7aa',
            borderRadius: '10px',
            padding: '12px 12px',
            color: '#f97316',
            fontSize: '12px',
            textAlign: 'center',
          }}>
            No options configured
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {options.map((option, index) => (
              <div
                key={option.id || index}
                style={{
                  background: '#fff7ed',
                  border: option.selected 
                    ? `2px solid ${accent}` 
                    : `1.5px solid #fed7aa`,
                  borderRadius: '8px',
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.1s',
                  cursor: 'default',
                }}
              >
                {/* Selection indicator */}
                <div style={{
                  width: selectionType === 'radio' ? '18px' : '16px',
                  height: selectionType === 'radio' ? '18px' : '16px',
                  borderRadius: selectionType === 'radio' ? '50%' : '4px',
                  border: `2px solid ${accent}`,
                  background: option.selected ? accent : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.1s',
                }}>
                  {selectionType === 'checkbox' && option.selected && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                {/* Option content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '13px',
                    color: '#7c2d12',
                    marginBottom: '4px',
                  }}>
                    {option.label || `Option ${index + 1}`}
                  </div>
                  
                  {/* Option metadata */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    fontSize: '11px',
                    color: '#b45309',
                    flexWrap: 'wrap',
                  }}>
                    {option.value && (
                      <span>Value: {option.value}</span>
                    )}
                    {option.weight && (
                      <span>Weight: {option.weight}</span>
                    )}
                    {/* Display costs if associated with this option */}
                    {option.costs && option.costs.length > 0 && option.costs.map((cost, idx) => (
                      <Chip
                        key={idx}
                        label={`${cost.name || 'Cost'}: ₹${cost.amount || cost.value?.amount || '0'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f97316',
                          color: 'white',
                          fontSize: '10px',
                          height: '20px',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Selected options summary */}
        {selectedOptions.length > 0 && (
          <div style={{
            marginTop: '12px',
            paddingTop: '10px',
            borderTop: '1px solid #fed7aa',
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#b45309',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Selected {selectionType === 'radio' ? 'Option' : 'Options'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedOptions.map((opt, idx) => (
                <Chip
                  key={idx}
                  label={opt.label}
                  size="small"
                  sx={{
                    backgroundColor: accent,
                    color: 'white',
                    fontSize: '10px',
                    height: '22px',
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Input/Output summary */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px',
          paddingTop: '10px',
          borderTop: '1px solid #fed7aa',
        }}>
          <Badge color={accent} bg="#fff7ed" label={`${inputs.length} Input${inputs.length !== 1 ? 's' : ''}`} />
          <Badge color="#10b981" bg="#ecfdf5" label={`${outputs.length} Output${outputs.length !== 1 ? 's' : ''}`} />
        </div>
      </div>

      {/* Right source handle (outgoing connection) */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          right: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />
    </div>
  );
};
// ─── Tiny helper components ───────────────────────────────────────────────────
const Badge = ({ label, color, bg }) => (
  <span style={{
    fontSize: '10px', fontWeight: 600, color, background: bg,
    borderRadius: '5px', padding: '2px 7px', border: `1px solid ${color}33`,
    whiteSpace: 'nowrap',
  }}>{label}</span>
);

const InfoRow = ({ icon, label, items, color }) => (
  <div style={{
    background: '#f8fafc', borderRadius: '8px',
    padding: '7px 10px', border: '1px solid #f1f5f9',
  }}>
    <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8',
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
      {icon} {label}
    </div>
    {items.map((name, i) => (
      <div key={i} style={{
        fontSize: '11.5px', color: '#334155', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: '5px',
        marginTop: i > 0 ? '3px' : 0,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%',
          background: color, flexShrink: 0 }} />
        {name}
      </div>
    ))}
  </div>
);


const nodeTypes = {
  default: CustomNodeComponent,
  diamond: DiamondNodeComponent,
  custom: CustomNodeComponent,
    option: OptionNodeComponent,
};

// Styled components
const Sidebar = styled(Box)(({ theme }) => ({
  width: 250,
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  justifyContent: 'space-between'
}));

const CanvasContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  height: "100vh",
}));

// Main Component
function FactoryLayout3rdStepContent() {
  const location2 = useLocation();
  const queryParams = new URLSearchParams(location2.search);
  const primaryid =  queryParams.get('primaryid');
  const secondaryid =  queryParams.get('secondaryid');
  const productNameFromUrl = queryParams.get('product_name');
  const clientNameFromUrl = queryParams.get('client_name');
  const clientIdFromUrl = queryParams.get('client_id');
  const finalProductIdFromUrl = queryParams.get('final_product_id');
  const variantIdFromUrl = queryParams.get('variant_id');

  const { screenToFlowPosition } = useReactFlow();
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = useRef(null);
const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);

  const [allVariantIds, setAllVariantIds] = useState([]);
const [showVariantDialog, setShowVariantDialog] = useState(false);
const [finalQuantity, setFinalQuantity] = useState(null);

  const [showDialog, setShowDialog] = useState(false);
  const [nodeColor, setNodeColor] = useState("#00c0ff");
  const [previousNodeInputs, setPreviousNodeInputs] = useState([]);
  const [costOptions, setCostOptions] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [workflowId, setworkflowId] = useState();
const [smartViewOpen, setSmartViewOpen] = useState(false);
  const [selectedmasterworkflow, setselectedmasterworkflow] = useState()
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectedEdges, setConnectedEdges] = useState([]);
  const [lastAddedNode, setLastAddedNode] = useState(null);
  const [isErrorEdgeMode, setIsErrorEdgeMode] = useState(false);
  const [errorSourceNode, setErrorSourceNode] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const [savedWorkflows, setSavedWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [calculateDialogOpen, setCalculateDialogOpen] = useState(false);
  const [calcualtionneededfields, setcalculationneededfields] = useState([])
  const [editNodeName, setEditNodeName] = useState("");
  const [editRawMaterialInputs, setEditRawMaterialInputs] = useState([]);
  const [editPreviousNodeInputs, setEditPreviousNodeInputs] = useState([]);
  const [editUtilityInputs, setEditUtilityInputs] = useState([]);
  const [editNodeOutput, setEditNodeOutput] = useState("");
  const [editNodeCost, setEditNodeCost] = useState([]);
  const [editNodeInputType, setEditNodeInputType] = useState("raw");
  const [inventoryData, setInventoryData] = useState([]);
  const [utilityData, setUtilityData] = useState([]);
  const [nodeListVersion, setNodeListVersion] = useState(0);
  const [showOutputDialog, setShowOutputDialog] = useState(false);
  const [editEdgeLabel, setEditEdgeLabel] = useState("");
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [editoutputids, seteditoutputids] = useState([])
  const [fetchedOutputs, setFetchedOutputs] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [allocatedoutputs, setAllocatedoutputs] = useState([]);
  const [showInputDialog, setshowInputDialog] = useState(false)
  const [prefilledOutput, setPrefilledOutput] = useState(0);
  const [isWorkflowLoaded, setIsWorkflowLoaded] = useState(false);
  const [variantoptions, setvariantoptions] = useState([])
  const [showOutputSelectionDialog, setShowOutputSelectionDialog] = useState(false);
  const [sourceNodeForEdge, setSourceNodeForEdge] = useState(null);
  const [targetNodeForEdge, setTargetNodeForEdge] = useState(null);
  const [edgeOutputs, setEdgeOutputs] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [edgeCreationType, setEdgeCreationType] = useState(null);
  const [nodeData, setNodeData] = useState(null);
  const [loadingNodeData, setLoadingNodeData] = useState(false);
  const [nodeDataCache, setNodeDataCache] = useState(new Map());
  const [nodefromworkflow, setnodefromworkflow] = useState([])
  const [utitilityfromdrawer, setutitilityfromdrawer] = useState([])
  const [issaveworkflowready, setissaveworkflowready] = useState(false)
  const [outputsavedforedges, setoutputsavedforedges] = useState([])
  const [clickednodes, setclickednodes] = useState([]);
  const [selectedworkflowvariant, setselectedworkflowvariant] = useState([])
  const [isEditing, setIsEditing] = useState(true)
  const [selectedVariantsfromparent, setselectedVariantsfromparent] = useState([])
  const [JobDialogOpen, setJobDialogOpen] = useState(false)
  const [updatedNodes, setUpdatedNodes] = useState([]);
  const [isUpdatingNodes, setIsUpdatingNodes] = useState(false);
  const [updatedNodeResponses, setUpdatedNodeResponses] = useState([]);
  const [finalProducts, setFinalProducts] = useState([]);
  const [selectedFinalProduct, setSelectedFinalProduct] = useState();

  const [resolvedProductName, setResolvedProductName] = useState('');
  const [resolvedClientName, setResolvedClientName] = useState('');
  const [clients, setClients] = useState([]);

  // Fetch client list
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axiosInstance.get(`${process.env.REACT_APP_HOST_API}/order-app/list/client/`);
        setClients(res.data || []);
      } catch (err) {
        console.error('Error fetching clients in 3rdStep:', err);
      }
    };
    fetchClients();
  }, []);

  // Resolve Product Name and Client Name
  useEffect(() => {
    const resolveNames = async () => {
      // 1. Product Name resolution
      if (productNameFromUrl) {
        setResolvedProductName(productNameFromUrl);
      } else if (nodefromworkflow?.name) {
        setResolvedProductName(nodefromworkflow.name);
      } else {
        const targetVariantId = variantIdFromUrl || (finalQuantity?.product_variant_ids && finalQuantity.product_variant_ids[0]);
        if (targetVariantId) {
          try {
            const variantsList = await fetcher(['/products-app/product-variant/list/']);
            if (Array.isArray(variantsList)) {
              const match = variantsList.find(
                (v) => v.id === parseInt(targetVariantId, 10) || v.id?.toString() === targetVariantId.toString()
              );
              if (match?.name) setResolvedProductName(match.name);
            }
          } catch (e) {
            console.error('Error resolving product name in 3rd step:', e);
          }
        }
      }

      // 2. Client Name resolution
      if (clientNameFromUrl) {
        setResolvedClientName(clientNameFromUrl);
      } else if (nodefromworkflow?.client?.name || nodefromworkflow?.client_name) {
        setResolvedClientName(nodefromworkflow.client?.name || nodefromworkflow.client_name);
      } else {
        const targetClientId = clientIdFromUrl || nodefromworkflow?.client_id || nodefromworkflow?.client;
        if (targetClientId && clients.length > 0) {
          const match = clients.find(
            (c) => c.id === parseInt(targetClientId, 10) || c.id?.toString() === targetClientId.toString()
          );
          if (match?.name) setResolvedClientName(match.name);
        }
      }
    };

    resolveNames();
  }, [productNameFromUrl, clientNameFromUrl, clientIdFromUrl, variantIdFromUrl, finalQuantity, nodefromworkflow, clients]);

  const defaultJobName = useMemo(() => {
    const pName = resolvedProductName || selectedWorkflow || selectedworkflowvariant || '';
    const cName = resolvedClientName || '';
    if (pName && cName) {
      return `${pName} - ${cName}`;
    }
    return pName || cName || '';
  }, [resolvedProductName, resolvedClientName, selectedWorkflow, selectedworkflowvariant]);

  const isErrorEdgeModeRef = useRef(false);
  const isWorkflowLoadedRef = useRef(isWorkflowLoaded);
  const errorSourceNodeRef = useRef(null);
  const handleNodeSelectionRef = useRef();
  const nodefromworkflowRef = useRef(nodefromworkflow);
  const route = useRouter();

  const handleCloseJobDialog = () => {
    setJobDialogOpen(false)
  }

  useEffect(() => {
    const hasMatch = nodes.some(node => clickednodes.includes(node.backendId));
    setissaveworkflowready(hasMatch);
  }, [nodes, clickednodes]);

  useEffect(() => {
    const backendId = selectedNode?.data?.backendId;
    if (clickednodes.includes(backendId)) {
      setIsEditing(true);
    } else {
      setIsEditing(true);
    }
  }, [selectedNode, clickednodes]);

  const handlefetchworkflowvariants = async (id) => {
    const response = await fetcher([`/new/workflow-app/process/in/workflow/?process_creation_id=${id}`])
    setvariantoptions(response)
  }

  useEffect(() => {
    if (selectedWorkflow) {
      const workflow = savedWorkflows.find((w) => w.name === selectedWorkflow);
      handlefetchworkflowvariants(workflow.id)
      setselectedmasterworkflow(workflow.id)
    }
  }, [selectedWorkflow])

  useEffect(() => {
    isErrorEdgeModeRef.current = isErrorEdgeMode;
    errorSourceNodeRef.current = errorSourceNode;
  }, [isErrorEdgeMode, errorSourceNode]);

  useEffect(() => {
    isWorkflowLoadedRef.current = isWorkflowLoaded;
  }, [isWorkflowLoaded]);

  useEffect(() => {
    nodefromworkflowRef.current = nodefromworkflow;
  }, [nodefromworkflow, isWorkflowLoaded]);

  // Fetch workflows from API on component mount
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const response = await postFetcher(['/new/workflow-app/all/processcreation/show/', { final_product_id: selectedFinalProduct}]);
        if (!response) {
          throw new Error('Failed to fetch workflows');
        }
        setSavedWorkflows(response);
      } catch (error) {
        enqueueSnackbar('Failed to load workflows from API', { variant: 'error' });
      }
    };

    fetchWorkflows();
  }, [selectedFinalProduct]);

  // Fetch final products from API on component mount
  useEffect(() => {
    const fetchFinalProducts = async () => {
      try {
        const response = await fetcher(['/new/workflow-app/final-product/dropdown/']);
        if (!response) {
          throw new Error('Failed to fetch final products');
        }
        setFinalProducts(response);
      } catch (error) {
        enqueueSnackbar('Failed to load final products from API', { variant: 'error' });
      }
    };

    fetchFinalProducts();
  }, []);

  useEffect(() => {
    if (prefilledOutput && edgeOutputs && showOutputSelectionDialog && previousNodeInputs.length > 0) {
      const related_obj = edgeOutputs.find((edgeOutput) => edgeOutput.output_id === prefilledOutput.outputId);
      setSelectedOutput(related_obj);

      let nodeState = null;

      if (isWorkflowLoaded) {
        const nodeLayer = nodefromworkflow?.model?.layers?.find(layer => layer.type === 'diagram-nodes');
        const nodesFromWorkflow = nodeLayer ? Object.values(nodeLayer.models) : [];
        const backendId = previousNodeInputs[0]?.backendId?.toString();

        if (!backendId) {
          setSourceNodeForEdge(null);
          setEdgeOutputs([]);
          setShowOutputSelectionDialog(false);
          return;
        }

        const nodeData = nodesFromWorkflow.find(n => n.backend_id?.toString() === backendId);
        if (nodeData) {
          nodeState = rfNodes.find(n => n.data.backendId?.toString() === backendId);
        }
      } else {
        const backendId = previousNodeInputs[0]?.backendId?.toString();
        if (!backendId) {
          setSourceNodeForEdge(null);
          setEdgeOutputs([]);
          setShowOutputSelectionDialog(false);
          return;
        }
        nodeState = rfNodes.find(n => n.data.backendId?.toString() === backendId);
      }

      if (nodeState) {
        setSourceNodeForEdge(nodeState);
        fetchEdgeOutputs(nodeState);
      } else {
        setSourceNodeForEdge(null);
        setEdgeOutputs([]);
        setShowOutputSelectionDialog(false);
      }
    }
  }, [prefilledOutput, edgeOutputs, showOutputSelectionDialog, previousNodeInputs, nodefromworkflow, rfNodes, isWorkflowLoaded]);

  const fetchInputInventory = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/product/list/']);
      setInventoryData(res);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchUtilityList = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/utility/list/']);
      setUtilityData(res);
    } catch (error) {
      console.error('Error fetching utility list:', error);
    }
  };

  const fetchCostList = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/costs/']);
      const formattedCosts = res.map(item => ({
        label: `${item.name} (${item.value.amount} ${item.value.unit} - ${item.value.type})`,
        value: item.id.toString(),
        ...item
      }));
      setCostOptions(formattedCosts);
    } catch (error) {
      console.error('Error fetching cost list:', error);
    }
  };

  const fetchOutputData = async () => {
    try {
      const savedOutputIds = JSON.parse(sessionStorage.getItem('savedOutputIds') || '[]');
      if (savedOutputIds.length === 0) {
        setFetchedOutputs([]);
        return;
      }
      const payload = { output_ids: savedOutputIds };
      const res = await postFetcher(["/new/workflow-app/output/list/", payload]);
      if (res) {
        const outputs = Array.isArray(res) ? res : res.data?.outputs || [];
        setFetchedOutputs(outputs);
      } else {
        setFetchedOutputs([]);
      }
    } catch (error) {
      console.error('Error fetching output data:', error);
      setFetchedOutputs([]);
    }
  };

  const handleCloseCalculateDialog = () => {
    setCalculateDialogOpen(false);
    setJobDialogOpen(true)
  };

  const fetchEdgeOutputs = useCallback(async (sourceNode) => {
    if (!sourceNode || !sourceNode.data) {
      setEdgeOutputs([]);
      return;
    }

    try {
      const sourceNodeBackendId = sourceNode.data.backendId?.toString();
      let sourceNodeData;

      await new Promise(resolve => setTimeout(resolve, 50));

      if (isWorkflowLoadedRef.current) {
        const nodeLayer = nodefromworkflowRef.current?.model?.layers?.find(
          layer => layer.type === "diagram-nodes"
        );
        const nodesFromWorkflow = nodeLayer ? Object.values(nodeLayer.models) : [];
        sourceNodeData = nodesFromWorkflow.find(
          n => n.backend_id?.toString() === sourceNodeBackendId
        );
      } else {
        sourceNodeData = nodes.find(
          n =>
            n.id?.toString() === sourceNodeBackendId ||
            n.backendId?.toString() === sourceNodeBackendId
        );
      }

      if (!sourceNodeData || !sourceNodeData.output_foreign || sourceNodeData.output_foreign.length === 0) {
        setEdgeOutputs([]);
        return;
      }

      const availableOutputIds = sourceNodeData.output_foreign.filter(
        id => !allocatedoutputs.includes(id.toString())
      );

      if (availableOutputIds.length === 0) {
        setEdgeOutputs([]);
        return;
      }

      let idsarray;
      if (typeof availableOutputIds[0] === 'object' && availableOutputIds[0] !== null) {
        idsarray = availableOutputIds.map(obj => obj.id);
      } else {
        idsarray = availableOutputIds;
      }

      const payload = { output_ids: idsarray };
      const res = await postFetcher(["/new/workflow-app/output/list/", payload]);
      const outputs = Array.isArray(res) ? res : res || [];

      setEdgeOutputs(outputs.map(output => ({
        label: output.output_name,
        value: output.output_id.toString(),
        ...output
      })));
      const mappedOutputs = outputs.map(output => ({
        label: output.output_name,
        value: output.output_id.toString(),
        ...output
      }));
      setoutputsavedforedges((prev) => [...prev, mappedOutputs]);
    } catch (error) {
      setEdgeOutputs([]);
    }
  }, [nodes, allocatedoutputs, nodefromworkflow]);

  useEffect(() => {
    if (!showOutputDialog) {
      fetchOutputData();
    }
  }, [showOutputDialog]);

  useEffect(() => {
    fetchInputInventory();
    fetchUtilityList();
    fetchCostList();
  }, []);

  useEffect(() => {
    if (showDialog) {
      fetchOutputData();
    }
  }, [showDialog]);

  const inventoryOptions = inventoryData.map(item => ({
    label: item.name,
    value: item.id.toString(),
    product: { id: item.id, name: item.name }
  }));

  const utilityOptions = utilityData.map(item => ({
    label: `${item.name} (Value: ${item.value?.quantity})`,
    value: item.id.toString(),
    ...item,
  }));

  const sourceNodeOptions = useMemo(() => {
    return rfNodes
      .filter(node => {
        const isDifferentNode = !drawerOpen || !selectedNode || node.id !== selectedNode.id;
        return isDifferentNode;
      })
      .map(node => ({
        label: node.data.label || "Unnamed Node",
        id: node.id,
        backendId: node.data.backendId
      }));
  }, [rfNodes, selectedNode, drawerOpen]);

  const nodeHasRawMaterials = async (node) => {
    if (!node || !node.data) {
      return false;
    }

    const backendId = node.data.backendId;
    if (!backendId) return false;

    try {
      const res = await fetcher([`/new/workflow-app/node/${backendId}/`]);
      const productIds = res?.input_foreign?.[0]?.value?.product_ids || [];
      return Array.isArray(productIds) && productIds.length > 0;
    } catch (error) {
      return false;
    }
  };

  const handleRemoveEdge = (edgeId) => {
    const edge = rfEdges.find(e => e.id === edgeId);
    
    if (edge) {
      const outputIdToFree = edge.data?.outputId;
      if (outputIdToFree) {
        setAllocatedoutputs(prev => prev.filter(id => id !== outputIdToFree));
      }
      
      setRfEdges(eds => eds.filter(e => e.id !== edgeId));

      if (selectedNode) {
        const remainingEdges = rfEdges.filter(e => e.id !== edgeId && (e.source === selectedNode.id || e.target === selectedNode.id));
        const edgeDetails = remainingEdges.map(e => {
          const sourceNode = rfNodes.find(n => n.id === e.source);
          const targetNode = rfNodes.find(n => n.id === e.target);
          
          return {
            id: e.id,
            sourceNode: sourceNode?.data.label || 'Unknown',
            sourcePort: 'Right',
            targetNode: targetNode?.data.label || 'Unknown',
            targetPort: 'Left',
            isErrorEdge: e.style?.stroke === 'red' || false,
            label: e.label || '',
            outputId: e.data?.outputId
          };
        });
        setConnectedEdges(edgeDetails);
      } else {
        setConnectedEdges([]);
      }

      setSelectedEdgeId(null);
      setEditEdgeLabel("");
      enqueueSnackbar(`Edge removed successfully!`, { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to remove edge!', { variant: 'error' });
    }
  };

  const handleoutputdialog = () => {
    setShowOutputDialog(true);
    sessionStorage.removeItem('savedOutputIds');
  };

  const handleinputformuladialog = () => {
    setshowInputDialog(true);
  };

  const handleAddOrEditEdgeLabel = (edgeId) => {
    if (!editEdgeLabel.trim()) {
      enqueueSnackbar('Please enter a valid label', { variant: 'error' });
      return;
    }

    setRfEdges(eds => eds.map(e => {
      if (e.id === edgeId) {
        return { ...e, label: editEdgeLabel };
      }
      return e;
    }));

    setConnectedEdges(prev => prev.map(edge =>
      edge.id === edgeId ? { ...edge, label: editEdgeLabel } : edge
    ));

    enqueueSnackbar(`Label "${editEdgeLabel}" updated for edge`, { variant: 'success' });
    setEditEdgeLabel("");
    setSelectedEdgeId(null);
  };

  const saveWorkflow = async () => {
    if (!workflowName.trim()) {
      enqueueSnackbar('Please enter a workflow name', { variant: 'error' });
      return;
    }

    const modelToSave = {
      nodes: rfNodes.map(node => ({
        id: node.data.backendId?.toString() || node.id,
        type: node.type,
        position: node.position,
        data: {
          ...node.data,
          backendId: node.data.backendId
        }
      })),
      edges: rfEdges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        style: edge.style,
        data: edge.data
      })),
      viewport: { x: 0, y: 0, zoom: 1 }
    };

    const updatedAvailableNodes = nodes.map(node => ({
      backendId: node.backendId,
      id: node.backendId?.toString() || node.id,
      name: node.name,
      color: node.color || '#00c0ff',
      type: node.type,
      input: node.input_foreign || [],
      output: node.output_foreign || [],
      cost: node.cost_foreign || [],
    }));

    const newWorkflow = {
      name: workflowName,
      model: modelToSave,
      availableNodes: updatedAvailableNodes,
      allocated_outputs: allocatedoutputs
    };

    try {
      const res = await postFetcher([
        `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/save/`,
        newWorkflow,
      ]);

      setSavedWorkflows(prev => [...prev, {
        name: workflowName,
        id: res.workflow_id,
        backendId: res.workflow_id,
      }]);
      setWorkflowName("");
      setShowSaveDialog(false);
      enqueueSnackbar(`Workflow "${workflowName}" saved successfully!`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error saving workflow!', { variant: 'error' });
      console.error(error);
    }
  };

  const fetchWorkflow = async (workflowId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/show/?workflow_creation_id=${workflowId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch workflow');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching workflow:', error);
      throw error;
    }
  };

  const fetchworkflowneed = async () => {
    const response = await fetcher([`/new/workflow-app/show/linked/attributes/from/ouput/wihtout/product/id/?workflow_id=${workflowId}`])
    setcalculationneededfields(response)
  }

  useEffect(() => {
    if (workflowId) {
      fetchworkflowneed()
    }
  }, [workflowId])




  

// Update the useEffect to handle both IDs
useEffect(() => {
  const autoLoadWorkflow = async () => {
    console.log("Auto-loading workflow with primaryid:", primaryid, "and secondaryid:", secondaryid);
    if (!primaryid) return;

    try {
      // 1. Ensure savedWorkflows list is loaded for the "Select Workflow" dropdown
      let workflowsList = savedWorkflows;
      if (!workflowsList || workflowsList.length === 0) {
        workflowsList = await postFetcher(['/new/workflow-app/all/processcreation/show/', { final_product_id: selectedFinalProduct }]);
        if (Array.isArray(workflowsList)) {
          setSavedWorkflows(workflowsList);
        }
      }

      // 2. Find and set master workflow for the "Select Workflow" dropdown
      const masterWf = Array.isArray(workflowsList)
        ? workflowsList.find((w) => w.id == primaryid || w.id?.toString() === primaryid.toString())
        : null;

      if (masterWf) {
        console.log("Pre-selecting master workflow in dropdown:", masterWf.name);
        setSelectedWorkflow(masterWf.name);
        setselectedmasterworkflow(masterWf.id);
      } else {
        setselectedmasterworkflow(primaryid);
      }

      // 3. Fetch variants for this process creation
      const response = await fetcher([`/new/workflow-app/process/in/workflow/?process_creation_id=${primaryid}`]);
      console.log("Fetched variants response in 3rd step autoLoadWorkflow:", response);

      if (Array.isArray(response) && response.length > 0) {
        setvariantoptions(response);

        // 4. Find matching variant for the "Select Workflow Variants" dropdown
        const variant = secondaryid
          ? response.find((v) => v.id == secondaryid || v.id?.toString() === secondaryid.toString())
          : response[response.length - 1] || response[0];
        
        if (variant) {
          console.log("Auto-selected workflow variant:", variant);
          setselectedworkflowvariant(variant.name);
          setTimeout(() => {
            loadWorkflow();
          }, 150);
        } else {
          console.error("Selected workflow variant not found for secondaryid:", secondaryid);
          loadWorkflow();
        }
      }
    } catch (err) {
      console.error("Error in autoLoadWorkflow:", err);
      loadWorkflow();
    }
  };
  
  autoLoadWorkflow();
}, [primaryid, secondaryid]);

// Updated loadWorkflow function
const loadWorkflow = async () => {
  console.log("Loading workflow with selectedworkflowvariant:", selectedworkflowvariant);
  
  // Prevent multiple simultaneous loads
  if (isLoadingWorkflow) {
    console.log("Workflow already loading, skipping...");
    return;
  }
  
  setIsLoadingWorkflow(true);
  
  let workflowVariantId;
  let workflowVariantName = selectedworkflowvariant;
  
  // If we have primaryid and secondaryid but no selectedworkflowvariant, try to find the variant directly
  if ( primaryid && secondaryid) {
    try {
      console.log("Fetching variants directly for primaryid:", primaryid);
      const variants = await fetcher([`/new/workflow-app/process/in/workflow/?process_creation_id=${primaryid}`]);
      console.log("Fetched variants:", variants);
      const matchingVariant = variants.find(v => v.id == secondaryid);
      if (matchingVariant) {
        workflowVariantName = matchingVariant.name;
        workflowVariantId = matchingVariant.id;
        setselectedworkflowvariant(workflowVariantName);
        setvariantoptions(variants);
        console.log("Found matching variant:", matchingVariant);
      } else {
        console.error("No matching variant found for secondaryid:", secondaryid);
        enqueueSnackbar("Selected workflow variant not found!", { variant: 'error' });
        setIsLoadingWorkflow(false);
        return;
      }
    } catch (err) {
      console.error("Failed to fetch variants in loadWorkflow:", err);
      enqueueSnackbar("Failed to fetch workflow variants", { variant: 'error' });
      setIsLoadingWorkflow(false);
      return;
    }
  }
  
  const variantNameToUse = workflowVariantName || selectedworkflowvariant;
  console.log("Final variant name to use for loading:", variantNameToUse);
  
  if (!variantNameToUse) {
    enqueueSnackbar("Please select a workflow variant to load", { variant: 'error' });
    setIsLoadingWorkflow(false);
    return;
  }
  
  setIsWorkflowLoaded(true);
  
  try {
    clearNodeDataCache();
    
    // Find the workflow variant by name or ID
    let workflow;
    if (workflowVariantId) {
      workflow = { id: workflowVariantId, name: variantNameToUse };
    } else {
      workflow = variantoptions.find((w) => w.name === variantNameToUse);
    }
    
    if (!workflow || !workflow.id) {
      console.error("Workflow variant not found with name:", variantNameToUse,workflow);
      enqueueSnackbar("Selected workflow variant not found or invalid!", { variant: 'error' });
      setIsWorkflowLoaded(false);
      setIsLoadingWorkflow(false);
      return;
    }

    console.log("Found workflow variant:", workflow);
    
    // Fetch the workflow data
    const backendData = await fetchWorkflow(workflow.id);
    
    if (!backendData) {
      enqueueSnackbar("Workflow data not found!", { variant: 'error' });
      setIsWorkflowLoaded(false);
      setIsLoadingWorkflow(false);
      return;
    }
    
    setFinalQuantity(backendData.final_quantity);
    setworkflowId(workflow.id);
    const allocated_datafrombackend = backendData.allocated_outputs;
    setAllocatedoutputs(allocated_datafrombackend || []);
    setnodefromworkflow(backendData);

    // Handle loading React Flow data or converting from old format
    if (backendData.model && backendData.model.nodes && backendData.model.edges) {
      // New format (React Flow)
      const loadedNodes = (backendData.model.nodes || []).map(node => {
        // Determine node type based on data.type
        const nodeType = node.data?.type === 'option' ? 'option' : 
                        node.data?.type === 'diamond' ? 'diamond' : 'custom';
        
        // Extract options configuration for option nodes
        const optionsConfig = node.data?.options_config || {};
        const selectedOptions = node.data?.selected_options || [];
        
        // Merge selected options with options
        if (nodeType === 'option' && optionsConfig.options) {
          optionsConfig.options = optionsConfig.options.map(opt => ({
            ...opt,
            selected: selectedOptions.some(
              selected => selected === opt.id || selected === opt.label
            )
          }));
        }

        return {
          ...node,
          type: nodeType,
          data: {
            ...node.data,
            color: nodeType === 'option' ? '#f97316' : 
                   nodeType === 'diamond' ? '#ff6b6b' : '#00c0ff',
            options_config: optionsConfig,
            selected_options: selectedOptions,
          }
        };
      });
      
      setRfNodes(loadedNodes);
      setRfEdges(backendData.model.edges || []);
      
    } else if (backendData.model && backendData.model.layers) {
      // Old format (react-diagrams) - Convert to React Flow format
      const newNodes = [];
      const newEdges = [];
      const idMap = new Map();

      backendData.model.layers.forEach(layer => {
        if (layer.type === 'diagram-nodes') {
          Object.entries(layer.models).forEach(([backendId, nodeData]) => {
            const reactId = generateCustomId('node');
            idMap.set(backendId, reactId);

            // Determine node type
            const nodeType = nodeData.type === 'diamond' ? 'diamond' : 
                            nodeData.type === 'option' ? 'option' : 'custom';
            
            // Extract options configuration
            const optionsConfig = nodeData.options_config || {};
            const selectedOptions = nodeData.selected_options || [];
            
            // Merge selected options with options
            if (nodeType === 'option' && optionsConfig.options) {
              optionsConfig.options = optionsConfig.options.map(opt => ({
                ...opt,
                selected: selectedOptions.some(
                  selected => selected === opt.id || selected === opt.label
                )
              }));
            }

            const newNode = {
              id: reactId,
              type: nodeType,
              position: { x: nodeData.x || 0, y: nodeData.y || 0 },
              data: { 
                label: nodeData.name || 'Node',
                backendId: parseInt(nodeData.backendId || nodeData.backend_id || backendId),
                color: nodeType === 'option' ? '#f97316' : 
                       nodeType === 'diamond' ? '#ff6b6b' : '#00c0ff',
                type: nodeData.type,
                options_config: optionsConfig,
                selected_options: selectedOptions,
                ...nodeData
              }
            };
            newNodes.push(newNode);
          });
        }

        if (layer.type === 'diagram-links') {
          Object.values(layer.models).forEach(linkData => {
            const sourceNodeId = linkData.source;
            const targetNodeId = linkData.target;
            
            if (idMap.has(sourceNodeId) && idMap.has(targetNodeId)) {
              const newEdge = {
                id: linkData.id || `e-${sourceNodeId}-${targetNodeId}`,
                source: idMap.get(sourceNodeId),
                target: idMap.get(targetNodeId),
                type: 'default',
                style: { stroke: linkData.color === 'red' ? 'red' : 'grey', strokeWidth: 1 },
                label: linkData.labels?.[0]?.label || '',
                data: { 
                  outputId: linkData.labels?.[0]?.outputId || linkData.outputId,
                  isErrorEdge: linkData.color === 'red'
                }
              };
              newEdges.push(newEdge);
            }
          });
        }
      });

      setRfNodes(newNodes);
      setRfEdges(newEdges);
      enqueueSnackbar("Converted legacy workflow to React Flow format.", { variant: 'info' });
    }

    // After setting nodes, fetch edge outputs for each node
    setTimeout(async () => {
      const currentNodes = rfNodes.length > 0 ? rfNodes : 
                          (backendData.model?.nodes || newNodes || []);
      
      for (const node of currentNodes) {
        try {
          await fetchEdgeOutputs(node);
        } catch (error) {
          console.error(`Failed to fetch edge outputs for node ${node.data?.label}:`, error);
        }
      }
    }, 100);

    // Update nodes state to match nodefromworkflow
    const workflowNodes = (backendData.availableNodes || []).map(node => ({
      id: node.backendId,
      backendId: node.backendId,
      name: node.name,
      type: node.type,
      input_foreign: node.input || [],
      output_foreign: node.output || [],
      cost_foreign: node.cost || [],
      options_config: node.options_config,
      selected_options: node.selected_options || [],
    }));
    setNodes(workflowNodes);

    // Update DOM elements with raw material indicators
    setTimeout(() => {
      const currentRfNodes = rfNodes.length > 0 ? rfNodes : 
                            (backendData.model?.nodes || newNodes || []);
      
      currentRfNodes.forEach((node) => {
        setTimeout(async () => {
          const nodeElement = document.querySelector(`[data-nodeid="${node.id}"]`);
          if (nodeElement) {
            nodeElement.classList.add('node-with-pointer');
            const hasRawMaterials = await nodeHasRawMaterials(node);
            if (hasRawMaterials) {
              nodeElement.classList.add('node-with-raw-material');
              const indicator = document.createElement('div');
              indicator.className = 'raw-material-indicator';
              indicator.title = 'Please Fill Variants';
              nodeElement.appendChild(indicator);
            }
          }
        }, 100);
      });
    }, 200);

    if (rfNodes.length > 0 || backendData.model?.nodes?.length > 0 || newNodes?.length > 0) {
      const firstNode = rfNodes[0] || backendData.model?.nodes[0] || newNodes[0];
      if (firstNode) {
        handleNodeSelection(firstNode);
      }
      // setSmartViewOpen(true);
      setCalculateDialogOpen(true);
    }

    enqueueSnackbar("Workflow loaded successfully!", { variant: 'success' });

  } catch (error) {
    console.error("Error loading workflow:", error);
    enqueueSnackbar("Error loading workflow!", { variant: 'error' });
    setIsWorkflowLoaded(false);
  } finally {
    setIsLoadingWorkflow(false);
  }
};

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setEditNodeName(node.data.label || '');

    setEditRawMaterialInputs([]);
    setEditPreviousNodeInputs([]);
    setEditUtilityInputs([]);
    setEditNodeOutput(node.data.output || "");
    setEditNodeCost([]);

    setSelectedEdgeId(null);
    setEditEdgeLabel("");

    const edges = rfEdges.filter(e => e.source === node.id || e.target === node.id);
    const edgeDetails = edges.map(edge => {
      const sourceNode = rfNodes.find(n => n.id === edge.source);
      const targetNode = rfNodes.find(n => n.id === edge.target);
      
      return {
        id: edge.id,
        sourceNode: sourceNode?.data.label || 'Unknown',
        sourcePort: 'Right',
        targetNode: targetNode?.data.label || 'Unknown',
        targetPort: 'Left',
        isErrorEdge: edge.style?.stroke === 'red' || false,
        label: edge.label || '',
        outputId: edge.data?.outputId
      };
    });

    setConnectedEdges(edgeDetails);
  
  };

  const fetchnodedata = async () => {
    const id = selectedNode?.data?.backendId;
    if (!id) {
      return;
    }

    if (nodeDataCache.has(id)) {
      const cachedData = nodeDataCache.get(id);
      setNodeData(cachedData);

      if (cachedData && cachedData.input_foreign && cachedData.input_foreign.length > 0) {
        const inputData = cachedData.input_foreign[0];

        if (inputData.value && inputData.value.product_ids) {
          const selectedRawMaterials = inventoryOptions.filter(option =>
            inputData.value.product_ids.includes(Number(option.value))
          );
          setEditRawMaterialInputs(selectedRawMaterials);
        }

        if (inputData.value && inputData.value.utility_ids) {
          const selectedUtilities = utilityOptions.filter(option =>
            inputData.value.utility_ids.includes(Number(option.id))
          );
          setEditUtilityInputs(selectedUtilities);
        }
      }

      if (cachedData.cost_foreign && cachedData.cost_foreign.length > 0) {
        const selectedCosts = costOptions.filter(option =>
          cachedData.cost_foreign.some(cost => cost.id === Number(option.id))
        );
        setEditNodeCost(selectedCosts || []);
      } else {
        setEditNodeCost([]);
      }

      return;
    }

    setLoadingNodeData(true);
    try {
      const res = await fetcher([`/new/workflow-app/node/${id}/`]);

      setNodeDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(id, res);
        return newCache;
      });
      setNodeData(res);

      if (res && res.input_foreign && res.input_foreign.length > 0) {
        const inputData = res.input_foreign[0];

        if (inputData.value && inputData.value.product_ids) {
          const selectedRawMaterials = inventoryOptions.filter(option =>
            inputData.value.product_ids.includes(Number(option.value))
          );
          setEditRawMaterialInputs(selectedRawMaterials);
        }

        if (inputData.value && inputData.value.utility_ids) {
          const selectedUtilities = utilityOptions.filter(option =>
            inputData.value.utility_ids.includes(Number(option.id))
          );
          setEditUtilityInputs(selectedUtilities);
        }
      }

      if (res.cost_foreign && res.cost_foreign.length > 0) {
        const selectedCosts = costOptions.filter(option =>
          res.cost_foreign.some(cost => cost.id === Number(option.id))
        );
        setEditNodeCost(selectedCosts || []);
      } else {
        setEditNodeCost([]);
      }
    } catch (error) {
      setEditNodeCost([]);
    } finally {
      setLoadingNodeData(false);
    }
  };

  useEffect(() => {
    if (drawerOpen && selectedNode) {
      fetchnodedata();
    }
  }, [drawerOpen, selectedNode]);

  const handleCreateErrorEdge = async (sourceNode, targetNode) => {
    if (!sourceNode || !targetNode || sourceNode.id === targetNode.id) {
      enqueueSnackbar("Invalid node selection for error edge!", { variant: 'error' });
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
      return;
    }

    try {
      const newEdge = {
        id: `e-${sourceNode.id}-${targetNode.id}-${Date.now()}`,
        source: sourceNode.id,
        target: targetNode.id,
        type: 'default',
        style: { stroke: 'red', strokeWidth: 1 },
        label: 'Error',
        data: { isErrorEdge: true }
      };

      setRfEdges((eds) => addEdge(newEdge, eds));

      enqueueSnackbar(`Error edge created from ${sourceNode.data.label} to ${targetNode.data.label}`, { variant: 'success' });

      if (selectedNode === sourceNode) {
        handleNodeClick(sourceNode);
      }
    } catch (error) {
      enqueueSnackbar("Failed to create error edge!", { variant: 'error' });
      console.error(error);
    }

    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
  };

  const handleNodeSelection = useCallback((node) => {
    if (isWorkflowLoaded) {
      handleNodeClick(node);
  
    } else if (isErrorEdgeModeRef.current && errorSourceNodeRef.current) {
      handleCreateErrorEdge(errorSourceNodeRef.current, node);
    } else {
      handleNodeClick(node);
  
    }
  }, []);

  useEffect(() => {
    handleNodeSelectionRef.current = handleNodeSelection;
  }, [handleNodeSelection]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const nodeData = JSON.parse(e.dataTransfer.getData('nodeData'));
    if (!nodeData) return;

    const position = screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });

    const newNode = {
      id: generateCustomId('node'),
      type: nodeData.type === 'diamond' ? 'diamond' : 'custom',
      position,
      data: { 
        label: nodeData.name || "New Node",
        backendId: nodeData.backendId || nodeData.backend_id,
        color: nodeData.color || (nodeData.type === 'diamond' ? '#ff6b6b' : '#00c0ff'),
        type: nodeData.type,
        rawInputs: nodeData.rawInputs || [],
        nodeInputs: nodeData.nodeInputs || [],
        utilityInputs: nodeData.utilityInputs || [],
        output: nodeData.output || '',
        cost: nodeData.cost || [],
        ...nodeData
      },
    };

    setRfNodes((nds) => nds.concat(newNode));
    setNodeListVersion(prev => prev + 1);
    setTargetNodeForEdge(newNode);

    setIsWorkflowLoaded(false);

    setTimeout(async () => {
      const nodeElement = document.querySelector(`[data-nodeid="${newNode.id}"]`);
      if (nodeElement) {
        nodeElement.classList.add('node-with-pointer');
        const hasRawMaterials = await nodeHasRawMaterials(newNode);
        if (hasRawMaterials) {
          nodeElement.classList.add('node-with-raw-material');
          const indicator = document.createElement('div');
          indicator.className = 'raw-material-indicator';
          indicator.title = 'Please Fill Variants';
          nodeElement.appendChild(indicator);
        }
      }
    }, 100);

    setLastAddedNode(newNode);
  };

  const clearNodeDataCache = () => {
    setNodeDataCache(new Map());
    setNodeData(null);
  };

const handleUpdateAllNodesFromCalculation = async (calculationResult) => {
  console.log('📊 [handleUpdateAllNodesFromCalculation] Starting with result:', calculationResult);
  
  if (!calculationResult?.breakdown?.length) {
    enqueueSnackbar("No calculation data", { variant: "warning" });
    return;
  }

  setIsUpdatingNodes(true);
  let updateCount = 0;
  let errorCount = 0;

  try {
    // Log all available nodes for debugging
    console.log('📊 [handleUpdateAllNodesFromCalculation] Available RF Nodes:', 
      rfNodes.map(n => ({ id: n.id, backendId: n.data.backendId, label: n.data.label }))
    );

    // Log the breakdown steps
    console.log('📊 [handleUpdateAllNodesFromCalculation] Breakdown steps:', 
      calculationResult.breakdown.map(step => ({
        nodeId: step.node?.id,
        nodeName: step.node?.name,
        requiredQty: step.required_quantity,
        computedQty: step.computed_quantity,
        hasMaterial: step.node?.inputs?.some(i => i.is_material)
      }))
    );

    // Update ALL nodes based on input type
    for (const step of calculationResult.breakdown) {
      const backendId = String(step.node?.id);
      if (!backendId) {
        console.warn('⚠️ No backendId for step:', step);
        continue;
      }

      // Find the node in rfNodes
      const rfNode = rfNodes.find(n => 
        String(n.data.backendId) === backendId
      );

      if (!rfNode) {
        console.warn(`⚠️ Node not found for backendId: ${backendId}`);
        errorCount++;
        continue;
      }

      console.log(`✅ Found node ${backendId}: ${rfNode.data.label}`);

      // Fetch fresh node data to check its current state
      const freshNodeData = await fetcher([`/new/workflow-app/node/${backendId}/`]);
      console.log(`📦 Fresh data for node ${backendId}:`, freshNodeData);

      // Check if this node has raw materials
      const hasRawMaterialInput = freshNodeData.input_foreign?.[0]?.value?.product_ids?.length > 0;
      console.log(`📦 Node ${backendId} has raw materials:`, hasRawMaterialInput);

      let payload = {};

      if (hasRawMaterialInput) {
        // This is a raw material node (like your first node)
        const requiredQty = step.required_quantity || step.computed_quantity || 0;
        payload = {
          inventory_quantities: [{
            quantity: requiredQty,
            variant: freshNodeData.input_foreign?.[0]?.value?.product_variant_ids?.[0] || null,
          }],
        };
        console.log(`📦 Raw material payload for ${backendId}:`, payload);
      } else {
        // This is a non-raw material node (like your 4th node)
        const requiredQty = step.required_quantity || step.computed_quantity || 0;
        
        // IMPORTANT: For non-raw material nodes, we need to update the output_quantity
        // The API expects this in the input_foreign_data
        payload = {
          output_quantity: requiredQty,
          // Also include existing inventory_quantities to preserve them
          inventory_quantities: freshNodeData.input_foreign?.[0]?.value?.inventory_quantities || []
        };
        console.log(`📦 Non-raw payload for ${backendId}:`, payload);
      }

      try {
        // Call handleUpdateNode with the payload
        await handleUpdateNode(payload, rfNode);
        updateCount++;
        console.log(`✅ Successfully updated node ${backendId}`);
      } catch (updateError) {
        console.error(`❌ Error updating node ${backendId}:`, updateError);
        errorCount++;
      }
    }

    if (errorCount === 0) {
      enqueueSnackbar(`✅ Updated ${updateCount} nodes successfully!`, {
        variant: "success"
      });
    } else {
      enqueueSnackbar(`⚠️ Updated ${updateCount} nodes with ${errorCount} errors`, {
        variant: "warning"
      });
    }

  } catch (err) {
    console.error("❌ Bulk update failed:", err);
    enqueueSnackbar("Bulk update failed: " + (err.message || "Unknown error"), { variant: "error" });
  } finally {
    setIsUpdatingNodes(false);
  }
};

  const updateNonRawMaterialNodes = async (updatedNodeId, inventoryPayload) => {
    setIsUpdatingNodes(true);
    try {
      const nonRawMaterialNodes = [];

      for (const node of rfNodes) {
        const nodeId = node.data.backendId;
        if (!nodeId || nodeId === updatedNodeId) continue;

        const hasRawMaterials = await nodeHasRawMaterials(node);
        if (!hasRawMaterials) {
          nonRawMaterialNodes.push(node);
        }
      }

      for (const node of nonRawMaterialNodes) {
        const nodeId = node.data.backendId;
        const currentNodeData = await fetcher([`/new/workflow-app/node/${nodeId}/`]);

        const formData = new FormData();
        formData.append('name', currentNodeData.name);

        formData.append('input_foreign_data', JSON.stringify({
          inventory_ids: [],
          inventory_quantities: [],
          name: currentNodeData.input_foreign?.[0]?.name || currentNodeData.name + " Input",
          product_ids: currentNodeData.input_foreign?.[0]?.value?.product_ids || [],
          utility_ids: currentNodeData.input_foreign?.[0]?.value?.utility_ids || [],
          product_variant_ids: currentNodeData.input_foreign?.[0]?.value?.product_variant_ids || [],
        }));

        formData.append('output_foreign_ids', JSON.stringify(
          currentNodeData.output_foreign?.map(output => output.id) || []
        ));

        formData.append('cost_foreign_ids', JSON.stringify(
          currentNodeData.cost_foreign?.map(cost => cost.id) || []
        ));

        formData.append('machine_ids', JSON.stringify(
          currentNodeData.machine?.map(machine => machine.id) || []
        ));
        formData.append('user_ids', JSON.stringify(currentNodeData?.users));

        if (currentNodeData.attachments?.length > 0) {
          for (const attachment of currentNodeData.attachments) {
            if (attachment.file) {
              if (typeof attachment.file === 'string' && attachment.file.startsWith('http')) {
                const response = await fetch(attachment.file);
                const blob = await response.blob();
                const file = new File([blob], attachment.file_name || 'file', { type: blob.type });
                formData.append('files', file);
              } else {
                formData.append('files', attachment.file);
              }
            }
          }
        }

        const res = await axiosInstance.patch(`/new/workflow-app/node/${nodeId}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const responseData = res.data;

        setRfNodes(nds => nds.map(n => {
          if (n.id === node.id) {
            return {
              ...n,
              data: {
                ...n.data,
                color: "#4CAF50"
              }
            };
          }
          return n;
        }));

        setNodes(prev => prev.map(n =>
          n.backendId === nodeId
            ? {
                ...n,
                input_foreign: responseData.input_foreign || [],
              }
            : n
        ));

        setUpdatedNodes(prev => {
          const updated = [...prev.filter(id => id !== nodeId), nodeId];
          return updated;
        });

        setclickednodes(prev => {
          const updated = prev.includes(nodeId) ? prev : [...prev, nodeId];
          return updated;
        });
      }

      enqueueSnackbar(`Auto-updated ${nonRawMaterialNodes.length} non-raw-material nodes`, { variant: 'success' });

    } catch (error) {
      console.error('Error updating non-raw-material nodes:', error);
      enqueueSnackbar('Error auto-updating non-raw-material nodes', { variant: 'error' });
    } finally {
      setIsUpdatingNodes(false);
    }
  };

const handleUpdateNode = async (inventoryPayload = {}, overrideNode = null) => {
  console.log('📦 [handleUpdateNode] Called with payload:', inventoryPayload);
  
  const nodeToUpdate = overrideNode || selectedNode;
  if (!nodeToUpdate) {
    console.warn('⚠️ No node to update');
    return;
  }

  const backendId = nodeToUpdate.data.backendId;
  if (!backendId) {
    console.warn('⚠️ Node has no backendId');
    return;
  }

  console.log('📦 Updating node:', {
    id: nodeToUpdate.id,
    backendId,
    label: nodeToUpdate.data.label
  });

  try {
    const freshNodeData = await fetcher([`/new/workflow-app/node/${backendId}/`]);
    console.log('📦 Fresh node data:', freshNodeData);

    const formData = new FormData();
    const nodeName = freshNodeData.name || "Untitled Node";
    formData.append('name', nodeName);

    const inputForeign = freshNodeData.input_foreign?.[0] || {};
    const currentValue = inputForeign.value || {};

    // Build the input_foreign_data object
    const inputForeignData = {
      inventory_ids: inventoryPayload.inventory_ids ?? currentValue.inventory_ids ?? [],
      inventory_quantities: inventoryPayload.inventory_quantities ?? currentValue.inventory_quantities ?? [],
      name: inputForeign.name || nodeName + " Input",
      product_ids: currentValue.product_ids || [],
      utility_ids: currentValue.utility_ids || [],
      product_variant_ids: currentValue.product_variant_ids || [],
    };

   
    if (inventoryPayload.output_quantity !== undefined) {
      inputForeignData.output_quantity = inventoryPayload.output_quantity;
      console.log(`📦 Setting output_quantity to: ${inventoryPayload.output_quantity}`);
    }

    console.log('📦 Input foreign data:', inputForeignData);
    formData.append('input_foreign_data', JSON.stringify(inputForeignData));

    // Preserve other relationships
    formData.append('output_foreign_ids', JSON.stringify(
      freshNodeData.output_foreign?.map(o => o.id) || []
    ));

    formData.append('cost_foreign_ids', JSON.stringify(
      freshNodeData.cost_foreign?.map(c => c.id) || []
    ));

    formData.append('machine_ids', JSON.stringify(
      freshNodeData.machine?.map(m => m.id) || []
    ));

    formData.append('user_ids', JSON.stringify(freshNodeData.users || []));

    // Handle attachments
    if (freshNodeData.attachments?.length > 0) {
      for (const att of freshNodeData.attachments) {
        if (att.file) {
          if (typeof att.file === 'string' && att.file.startsWith('http')) {
            try {
              const res = await fetch(att.file);
              const blob = await res.blob();
              const file = new File([blob], att.file_name || 'file', { type: blob.type });
              formData.append('files', file);
            } catch (fileError) {
              console.error('Error fetching attachment:', fileError);
            }
          } else {
            formData.append('files', att.file);
          }
        }
      }
    }

    console.log('📦 Sending PATCH request to:', `/new/workflow-app/nodes/update/${backendId}/`);
    
    // Log FormData contents for debugging
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const res = await axiosInstance.patch(`/new/workflow-app/nodes/update/${backendId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const updatedData = res.data;
    console.log('✅ Update successful:', updatedData);

    setUpdatedNodeResponses(prev => [...prev, updatedData]);

    // Update the node color in React Flow
    setRfNodes(nds => nds.map(n => {
      if (n.id === nodeToUpdate.id) {
        return {
          ...n,
          data: {
            ...n.data,
            color: "#4CAF50"
          }
        };
      }
      return n;
    }));

    // Update nodes state
    setNodes(prev => prev.map(n =>
      n.backendId === backendId
        ? { 
            ...n, 
            name: nodeName, 
            color: "#4CAF50", 
            input_foreign: updatedData.input_foreign || [] 
          }
        : n
    ));

    setUpdatedNodes(prev => [...new Set([...prev, backendId])]);
    setclickednodes(prev => prev.includes(backendId) ? prev : [...prev, backendId]);

    return updatedData;

  } catch (err) {
    console.error("❌ Failed to update node:", err);
    if (err.response) {
      console.error("Error response:", err.response.data);
    }
    throw err;
  }
};

  const handleOpenJobDialogForAllNodes = () => {
    if (updatedNodes.length === 0) {
      enqueueSnackbar('No nodes have been updated yet', { variant: 'warning' });
      return;
    }
    setJobDialogOpen(true);
  };

  const onConnect = useCallback((params) => {
    const sourceNode = rfNodes.find(n => n.id === params.source);
    const targetNode = rfNodes.find(n => n.id === params.target);

    if (isErrorEdgeMode) {
      handleCreateErrorEdge(sourceNode, targetNode);
      return;
    }

    setSourceNodeForEdge(sourceNode);
    setTargetNodeForEdge(targetNode);
    setEdgeCreationType('manual');
    
    fetchEdgeOutputs(sourceNode).then(() => {
      sessionStorage.setItem('pendingLinkId', `e-${params.source}-${params.target}-${Date.now()}`);
      setShowOutputSelectionDialog(true);
    }).catch(error => {
      console.error('Error fetching outputs:', error);
    });
  }, [isErrorEdgeMode, rfNodes]);

  const onNodeClickHandler = useCallback((event, node) => {
    handleNodeSelection(node);
  }, [handleNodeSelection]);

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
  }, []);

  const handleCancelOutputSelection = () => {
    if (edgeCreationType === 'manual') {
      const pendingLinkId = sessionStorage.getItem('pendingLinkId');
      if (pendingLinkId) {
        setRfEdges(eds => eds.filter(e => e.id !== pendingLinkId));
      }
    }

    sessionStorage.removeItem('pendingLinkId');
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
    setPrefilledOutput(0);

    if (isErrorEdgeMode) {
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
    }
  };

  const handleConfirmOutputSelection = () => {
    if (!selectedOutput) {
      enqueueSnackbar('Please select an output for the edge', { variant: 'error' });
      return;
    }
    
    if (edgeCreationType !== 'error') {
      setAllocatedoutputs(prev => [...prev, selectedOutput.value]);
    }

    if (sourceNodeForEdge && targetNodeForEdge) {
      handleCreateEdgeWithOutput(sourceNodeForEdge, targetNodeForEdge, false);
    }

    sessionStorage.removeItem('pendingLinkId');
    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
  };

  const handleCreateEdgeWithOutput = (sourceNode, targetNode, isErrorEdge = false) => {
    if (!sourceNode || !targetNode || sourceNode.id === targetNode.id) {
      enqueueSnackbar("Invalid node selection for edge!", { variant: 'error' });
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
      setShowOutputSelectionDialog(false);
      setSourceNodeForEdge(null);
      setTargetNodeForEdge(null);
      setSelectedOutput(null);
      setEdgeCreationType(null);
      return;
    }

    try {
      const newEdge = {
        id: `e-${sourceNode.id}-${targetNode.id}-${Date.now()}`,
        source: sourceNode.id,
        target: targetNode.id,
        type: 'default',
        style: { stroke: isErrorEdge ? 'red' : 'grey', strokeWidth: 1 },
        label: isErrorEdge ? 'Error' : (selectedOutput?.label || ''),
        data: { 
          outputId: selectedOutput?.value,
          ...(isErrorEdge ? { isErrorEdge: true } : {})
        }
      };

      setRfEdges((eds) => addEdge(newEdge, eds));

      enqueueSnackbar(`Edge created from ${sourceNode.data.label} to ${targetNode.data.label}${selectedOutput ? ` with output "${selectedOutput.label}"` : ''}`, { variant: 'success' });

      if (selectedNode === sourceNode) {
        handleNodeClick(sourceNode);
      }
    } catch (error) {
      enqueueSnackbar("Failed to create edge!", { variant: 'error' });
      console.error(error);
    }

    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
  };

  const getConnectionMessage = () => {
    if (sourceNodeForEdge && targetNodeForEdge) {
      return `Select an output from ${sourceNodeForEdge.data?.label || 'Unknown'} to connect to ${targetNodeForEdge.data?.label || 'Unknown'}`;
    }
    if (sourceNodeForEdge) {
      return `Select an output from ${sourceNodeForEdge.data?.label || 'Unknown'} to connect to new node`;
    }
    return 'Please wait, loading node information...';
  };

  // Create a mock engineRef for compatibility with components that might expect it
  const mockEngineRef = useRef({
    getModel: () => ({
      getNodes: () => rfNodes.map(node => ({
        getID: () => node.id,
        getOptions: () => node.data,
        getPorts: () => ({}),
        getOutPorts: () => [],
        getInPorts: () => [],
        getPort: () => null
      })),
      getLinks: () => rfEdges
    })
  });

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar>
        <Box>
          <Typography variant="h6" gutterBottom>
            Production 3rd Step
          </Typography>

          {/* <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={() => setShowSaveDialog(true)}
            sx={{ mb: 1 }}
            disabled={!issaveworkflowready}
          >
            Save Workflow
          </Button> */}

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel>Select Final Product</InputLabel>
            <Select
              value={selectedFinalProduct}
              onChange={(e) => setSelectedFinalProduct(e.target.value)}
              label="Select Final Product"
            >
              <MenuItem value="" disabled>Select Final Product</MenuItem>
              {finalProducts?.map(finalProduct => (
                <MenuItem key={finalProduct.id} value={finalProduct.id}>
                  {finalProduct.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 1,
        background: smartViewOpen ? '#3b82f6' : 'transparent',
        border: `2px solid ${smartViewOpen ? '#3b82f6' : '#e4e4e7'}`,
        borderRadius: '50px',
        cursor: isWorkflowLoaded ? 'pointer' : 'not-allowed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
        opacity: isWorkflowLoaded ? 1 : 0.55,
        '&:hover': {
          background: smartViewOpen ? '#3b82f6' : '#f4f4f5',
        }
      }}
      onClick={() => isWorkflowLoaded && setSmartViewOpen(!smartViewOpen)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify 
          icon={smartViewOpen ? "mdi:eye" : "mdi:eye-outline"} 
          width={20}
          sx={{ color: smartViewOpen ? '#fff' : '#71717a' }}
        />
        <Typography sx={{ 
          fontWeight: 600, 
          fontSize: '13px',
          color: smartViewOpen ? '#fff' : 'text.primary'
        }}>
          {smartViewOpen ? 'Smart View Active' : 'Switch to Smart View'}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: smartViewOpen ? '#fff' : '#e4e4e7',
          transition: 'all 0.2s',
        }}
      />
    </Paper>
          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel>Select Workflow</InputLabel>
            <Select
              value={selectedWorkflow}
              onChange={(e) => setSelectedWorkflow(e.target.value)}
              label="Select Workflow"
            >
              <MenuItem value="" disabled>Select Workflow</MenuItem>
              {savedWorkflows?.map(workflow => (
                <MenuItem key={workflow.id} value={workflow.name}>
                  {workflow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel>Select Workflow Variants</InputLabel>
            <Select
              value={selectedworkflowvariant}
              onChange={(e) => setselectedworkflowvariant(e.target.value)}
              label="Select Workflow Variants"
            >
              <MenuItem value="" disabled>Select Workflow Variant</MenuItem>
              {variantoptions?.map(workflow => (
                <MenuItem key={workflow.id} value={workflow.name}>
                  {workflow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="inherit"
            fullWidth
            onClick={loadWorkflow}
            disabled={!selectedWorkflow}
            sx={{ mb: 2 }}
          >
            Load Workflow
          </Button>

          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleOpenJobDialogForAllNodes}
            disabled={updatedNodes.length === 0}
            sx={{ mb: 2 }}
            startIcon={<Iconify icon="mdi:calendar-plus" />}
          >
            Schedule All Nodes
          </Button>
          
          <Button variant='contained' color='inherit' disabled={!workflowId} fullWidth onClick={() => { setCalculateDialogOpen(true) }}>Calculate</Button>
          <Divider />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }} >
          <Button color="secondary" variant="contained" onClick={() => route.push('/production2ndstep')} startIcon={<Iconify icon="mingcute:arrow-left-fill" />} sx={{ fontSize: '0.7rem' }}>2nd Step </Button>
        </Box>
      </Sidebar>

      <CanvasContainer>
        <Box
          onDragOver={isWorkflowLoaded ? null : handleDragOver}
          onDrop={isWorkflowLoaded ? null : handleDrop}
          sx={{
            height: "100%",
            border: 1,
            borderColor: "grey.300",
            bgcolor: "grey.50",
            position: "relative",
          }}
        >
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClickHandler}
            onInit={onInit}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            nodesDraggable={!isWorkflowLoaded}
            nodesConnectable={!isWorkflowLoaded}
            elementsSelectable={true}
          >
            <Background />
            <Controls />
          </ReactFlow>

          {isUpdatingNodes && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
              <Typography variant="h6" color="primary" fontWeight="bold">
                Updating Connected Nodes...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we update all nonvariant connected nodes
              </Typography>
            </Box>
          )}

          {isDragging && !isWorkflowLoaded && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(0, 123, 255, 0.1)",
                border: 2,
                borderColor: "primary.main",
                borderStyle: "dashed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 100,
              }}
            >
              <Typography color="primary.main" fontWeight="bold">
                Drop here to add node
              </Typography>
            </Box>
          )}
        </Box>
      </CanvasContainer>
<SmartView3rdStep
  nodes={nodes}
  rfEdges={rfEdges}
  rfNodes={rfNodes}
  open={smartViewOpen}
  onClose={() => setSmartViewOpen(false)}
  onNodeUpdate={(node, updatedData) => {
    // Update the node in rfNodes when changes are made in SmartView
    setRfNodes(prevNodes => 
      prevNodes.map(n => {
        if (n.id === node.id) {
          return {
            ...n,
            data: {
              ...n.data,
              ...updatedData,
            }
          };
        }
        return n;
      })
    );
  }}
  allVariantIds={allVariantIds}
  setAllVariantIds={setAllVariantIds}
  setissaveworkflowready={setissaveworkflowready}
  setShowVariantDialog={setShowVariantDialog}
  finalQuantity={finalQuantity}
  issaveworkflowready={issaveworkflowready}
  setShowSaveDialog={setShowSaveDialog}
  workflowId={workflowId}
  selectedmasterworkflow={selectedmasterworkflow}
/>
      <NodePropertiesDrawer3rdStep
        key={selectedNode?.data?.backendId || 'drawer'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedEdgeId(null);
          setEditEdgeLabel("");
        }}
        selectedNode={selectedNode}
        nodeData={nodeData}
        loadingNodeData={loadingNodeData}
        editNodeName={editNodeName}
        setEditNodeName={setEditNodeName}
        editNodeInputType={editNodeInputType}
        setEditNodeInputType={setEditNodeInputType}
        editRawMaterialInputs={editRawMaterialInputs}
        setEditRawMaterialInputs={setEditRawMaterialInputs}
        editPreviousNodeInputs={editPreviousNodeInputs}
        setEditPreviousNodeInputs={setEditPreviousNodeInputs}
        editUtilityInputs={editUtilityInputs}
        setEditUtilityInputs={setEditUtilityInputs}
        editNodeCost={editNodeCost}
        setEditNodeCost={setEditNodeCost}
        inventoryOptions={inventoryOptions}
        utilityOptions={utilityOptions}
        costOptions={costOptions}
        sourceNodeOptions={sourceNodeOptions}
        connectedEdges={connectedEdges}
        selectedEdgeId={selectedEdgeId}
        setSelectedEdgeId={setSelectedEdgeId}
        handleUpdateNode={handleUpdateNode}
        setselectedVariantsfromparent={setselectedVariantsfromparent}
        selectedVariantsfromparent={selectedVariantsfromparent}
        seteditoutputids={seteditoutputids}
        editoutputids={editoutputids}
        setutitilityfromdrawer={setutitilityfromdrawer}
        setissaveworkflowready={setissaveworkflowready}
        nodes={nodes}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        clickednodes={clickednodes}
        setclickednodes={setclickednodes}
        setNodeColor={setNodeColor}
      />

      <SaveWorkflowDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        workflowName={workflowName}
        setWorkflowName={setWorkflowName}
        onSave={saveWorkflow}
      />
      
      <JobDialog
        JobDialogOpen={JobDialogOpen}
        handleCloseJobDialog={handleCloseJobDialog}
        workflowId={workflowId}
        nodeIds={updatedNodes}
        nodesData={updatedNodeResponses}
        defaultJobName={defaultJobName}
      />

      <CalculateDialog 
        CalculateDialogOpen={calculateDialogOpen} 
        handleCloseCalculateDialog={handleCloseCalculateDialog} 
        workflowId={workflowId} 
        calcualtionneededfields={calcualtionneededfields} 
        selectedmasterworkflow={selectedmasterworkflow} 
        handleUpdateNode={handleUpdateNode} 
        isUpdatingNodes={isUpdatingNodes} 
        onUpdateAllNodes={handleUpdateAllNodesFromCalculation} 
        setJobDialogOpen={setJobDialogOpen}
        variantIdFromUrl={variantIdFromUrl}
      />

      <Dialog
        open={showOutputSelectionDialog}
        onClose={handleCancelOutputSelection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Output for Edge</DialogTitle>
        <DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getConnectionMessage()}
          </Typography>
          <Autocomplete
            options={edgeOutputs.filter(output => !allocatedoutputs.includes(output.value))}
            getOptionLabel={(option) => option.label || 'Unnamed Output'}
            value={selectedOutput}
            disabled={!!prefilledOutput}
            onChange={(event, newValue) => setSelectedOutput(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Output"
                variant="outlined"
                helperText={edgeOutputs.filter(output => !allocatedoutputs.includes(output.value)).length === 0
                  ? "No outputs available for this node"
                  : "Choose an output for the edge"}
              />
            )}
            fullWidth
          />
        </DialogContentText>
        <DialogActions>
          <Button onClick={handleCancelOutputSelection} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOutputSelection}
            variant="contained"
            color="primary"
            disabled={!selectedOutput || !sourceNodeForEdge}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function FactoryLayout3rdStep() {
  return (
    <ReactFlowProvider>
      <FactoryLayout3rdStepContent />
    </ReactFlowProvider>
  );
}